import InputClearIcon from '@ebg/vue-common/src/components/common/InputCleareIcon/InputClearIcon.vue';
import Tag from '@ebg/vue-common/src/components/common/Tag/Tag.vue';

export default {
  name: 'AdvancedSelectExtended',
  components: {
    InputClearIcon,
    Tag
  },
  emits: ['select', 'deselect', 'input', 'change', 'dropdownShow', 'dropdownHide', 'reset', 'searchTextChange', 'selectedItemsChange'],
  props: {
    id: String,
    items: Array,
    tagsNoDeselect: Boolean,
    searchable: Boolean,
    disabled: Boolean,
    limitedWrapperSize: {
      type: Boolean,
      default: false
    },
    showMainTooltip: {
      type: Boolean,
      default: false
    },
    showOptionTooltip: {
      type: Boolean,
      default: false
    },
    upperCase: {
      type: Boolean,
      default: false
    },
    dropdownAlign: {
      type: String,
      default: 'left'
    },
    dropdownWrapperClass: {
      type: [String, Array, Object]
    },
    controlClass: {
      type: [String, Array, Object]
    },
    value: {
      type: [String, Number, Array]
    },
    customSearchHandler: {
      type: Function,
      default: null
    },
    valueField: {
      type: String,
      default: 'value'
    },
    textField: {
      type: String,
      default: 'text'
    },
    selectedTextField: {
      type: String,
      default: null
    },
    emptyListText: {
      type: String,
      default: 'advanced-select.not_found'
    },
    multiple: Boolean,
    searchPlaceholder: {
      type: String,
      default: 'advanced-select.search'
    },
    closeOnSelect: {
      type: Boolean | Function,
      default: true
    },
    taggable: {
      type: Boolean,
      default: true
    },
    multipleSelectedOptionsText: {
      type: Function,
      default: () => '{pluralizedSelectedText} {itemCount} {pluralizedCountText}'
    },
    autoAdjustValuesToItems: {
      type: Boolean,
      default: true
    },
    hasError: Boolean,
    hideClearIcon: Boolean,
    isLoadingOption: Boolean,
    showTagTooltip: Boolean,
    size: String,
    sortByAbc: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: null
    },
    sortExactSearchResultsAtTop: Boolean,
    autoSelectOnlyOptionOnSearch: Boolean,
    multipleAddNewOption: {
      type: Boolean,
      default: false
    },
    getItemClass: {
      type: Function,
      default: (item) => ''
    },
    inputClass: {
      type: String,
      default: ''
    },
    tagBgColor: {
      type: [String, Function],
      default: () => 'bg-secondary'
    },
    canRemoveItem: {
      type: Function,
      default: () => true
    },
    splitter: {
      type: String,
      default: ''
    },
    tagDisplayLimit: {
      type: Number,
      default: 5
    },
    comparisonMode: {
      type: String,
      default: ''
    },
    allowCustomValue: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      latestChangedValue: [],
      searchText: !this.multiple ? this.getItemTextBasedOnItemValue(this.value) : '',
      isDropdownVisible: false,
      selectedItemsMap: {},
      activeOptionIndex: null,
      isSearchStarted: false,
      formattedMultipleSelectedOptionsText: '',
      currentItem: null,
      currentComparisonMode: this.comparisonMode,
      number: 1,
      temporaryOption: []
    }
  },

  methods: {
    getTagBgColor (item) {
      if (typeof this.tagBgColor === 'function') {
        return this.tagBgColor(item);
      }
      return this.tagBgColor;
    },
    isItemSelected (item) {
      return Object.prototype.hasOwnProperty.call(this.selectedItemsMap, JSON.stringify(this.getItemValue(item)));
    },
    reset () {
      this.selectedItemsMap = {};
      this.searchText = '';
      this.emitInput();
      this.$emit('select', null);
      this.emitSelectedItemsChange();
      this.$emit('reset', null);
    },
    wrongValueTypeFallback (value) {
      if (!this.multiple && Array.isArray(value)) {
        console.warn('AdvancedSelect: v-model value type is array, but multiple is false, you should set multiple to true, or use v-model with correct type');
        return [];
      }

      return value;
    },
    emitSelectedItemsChange () {
      if (this.multiple) {
        this.$emit('selectedItemsChange', Object.values(this.selectedItemsMap));
      }
    },
    toggleComparisonMode () {
      this.currentComparisonMode = this.currentComparisonMode === '=' ? '!=' : '=';
      this.$emit('comparisonModeChange', this.currentComparisonMode);
    },
    getItemTextBasedOnItemValue (value) {
      const _value = this.wrongValueTypeFallback(value);
      const item = this.items.find((item) => this.getItemValue(item) === _value);
      return this.getItemText(item);
    },
    getItemText (item, mode = null) {
      if (!item) {
        return '';
      }
      if (mode === 'set-selected' && this.selectedTextField && item[this.selectedTextField]) {
        return item[this.selectedTextField];
      } else {
        return item[this.textField];
      }
    },
    getItemValue (item) {
      if (!item) {
        return '';
      }
      return item[this.valueField];
    },
    handleKeyDown (event) {
      if (!this.isDropdownVisible || this.isLoadingOption) {
        return;
      }
      const keyCode = event.code || event.key;
      if (keyCode === 'ArrowDown') {
        event.preventDefault();
        this.moveSelectorDown();
      } else if (keyCode === 'ArrowUp') {
        event.preventDefault();
        this.moveSelectorUp();
      } else if (keyCode === 'Tab' || keyCode === 'Enter') {
        if (this.activeOptionIndex !== null) {
          event.preventDefault();
          this.handleItemToggle(this.filteredItems[this.activeOptionIndex]);
        } else if (this.filteredItems.length === 1 && !this.isItemSelected(this.filteredItems[0])) {
          this.handleItemToggle(this.filteredItems[0]);
        }
      } else if (keyCode === 'Escape') {
        this.hideDropdown();
      }
    },
    moveSelectorDown () {
      const maxIndex = this.filteredItems.length - 1;
      if (this.activeOptionIndex === null || this.activeOptionIndex === maxIndex) {
        this.activeOptionIndex = 0;
      } else {
        this.activeOptionIndex++;
      }

      this.scrollToActiveOption();
    },
    selectAllFiltered () {
      this.filteredItems.forEach(item => {
        if (!this.isItemSelected(item)) {
          this.handleItemToggle(item, false);
        }
      });
      this.emitInput();
    },
    deselectAllFiltered () {
      this.filteredItems.forEach(item => {
        if (this.isItemSelected(item)) {
          this.handleItemToggle(item, false);
        }
      });
      this.emitInput();
    },
    moveSelectorUp () {
      const maxIndex = this.filteredItems.length - 1;
      if (this.activeOptionIndex === null || this.activeOptionIndex === 0) {
        this.activeOptionIndex = maxIndex;
      } else {
        this.activeOptionIndex--;
      }

      this.scrollToActiveOption();
    },
    scrollToActiveOption () {
      const activeOption = this.$refs.option[this.activeOptionIndex];
      const dropdownWrapper = this.$refs.dropdownWrapper;

      if (dropdownWrapper.scrollTop + dropdownWrapper.offsetHeight < activeOption.offsetTop + activeOption.offsetHeight) {
        dropdownWrapper.scrollTo({ top: activeOption.offsetTop + activeOption.offsetHeight - dropdownWrapper.offsetHeight });
      } else if (dropdownWrapper.scrollTop > activeOption.offsetTop) {
        dropdownWrapper.scrollTo({ top: activeOption.offsetTop });
      }
    },
    setSelectedItems () {
      let selectedValues = this.wrongValueTypeFallback(this.value);
      selectedValues = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
      const itemsMap = {};
      const selectedItems = [];
      this.items.forEach(item => {
        itemsMap[this.getItemValue(item)] = item;
      })
      this.selectedItemsMap = {};
      selectedValues.forEach(value => {
        this.setValueInMap(value, itemsMap[value]);
        selectedItems.push(itemsMap[value]);
      })

      if (!this.multiple) {
        this.searchText = this.getItemText(itemsMap[this.value], 'set-selected');
      }
      this.latestChangedValue = this.value;
      this.formattedMultipleSelectedOptionsText = this.getMultipleOptionsText();
      this.$forceUpdate();
      if (!this.multiple) {
        this.$emit('select', selectedItems[0] || null);
      }
      this.emitSelectedItemsChange();
    },

    showDropdown () {
      if (this.disabled || this.isDropdownVisible) {
        return;
      }
      this.isSearchStarted = false;
      this.isDropdownVisible = true;
      if (this.searchable) {
        this.$refs.searchInput.focus();
        this.$emit('searchTextChange', this.searchText)
      }
    },

    hideDropdown () {
      document.activeElement.blur();
      this.activeOptionIndex = null;
      this.isDropdownVisible = false;

      const searchText = this.searchText;

      if (!this.multiple) {
        this.searchText = this.getItemText(Object.values(this.selectedItemsMap)[0], 'set-selected');
      } else {
        this.searchText = '';
      }

      if (this.allowCustomValue && !this.multiple && !Number.isInteger(parseInt(Object.keys(this.selectedItemsMap)[0]))) {
        this.selectedItemsMap = {
          [searchText]: {
            id: searchText,
            title: searchText,
            text: searchText,
            value: searchText
          }
        }

        if (searchText && !this.items.some(item => item.id === searchText)) {
          this.items.push({ id: searchText, title: searchText });
        }

        this.searchText = searchText;
        this.$emit('input', searchText);
        this.$emit('searchTextChange', searchText);
        this.$emit('select', searchText || null);
      }
    },
    handleFocusOut () {
      // Don't close dropdown on focus change within component
      if (this.$refs.formControl.matches(':focus-within')) {
        return;
      }

      this.hideDropdown();
    },
    handleItemToggle (item, emitInput = true) {
      if (this.disabled || item.disabled) {
        return;
      }

      const itemValue = this.getItemValue(item);
      if (this.multiple) {
        if (!this.isItemSelected(item)) {
          if (this.handleSplitInput(item, itemValue)) return;
          // Ja ir norādīts limit
          if (this.limit) {
            // Ja izvēlēto opciju daudzums nepārsniedz limitu
            if (!(this.selectedItemsMap && (Object.keys(this.selectedItemsMap).length >= this.limit))) {
              this.setValueInMap(itemValue, item);
              this.$emit('select', item);
            } else {
              // Ja pārsniedz limitu, bet limits ir 1
              if (this.selectedItemsMap && (Object.keys(this.selectedItemsMap).length === 1) && (this.limit === 1)) {
                const prevItem = Object.values(this.selectedItemsMap)[0];
                const prevItemValue = this.value.pop();
                if (this.canRemoveItem(prevItem)) {
                  this.removeValueFromMap(prevItemValue)
                  this.setValueInMap(itemValue, item);
                  this.$emit('select', item);
                }
              }
            }
          } else {
            this.setValueInMap(itemValue, item);
            this.$emit('select', item);
          }
        } else {
          if (this.canRemoveItem(item)) {
            this.removeValueFromMap(itemValue)
            this.$emit('deselect', item);
          }
        }
        this.emitSelectedItemsChange();
      } else {
        this.selectedItemsMap = {};
        this.setValueInMap(itemValue, item);
        this.$emit('select', item);
      }

      const closeDropdown = this.evaluateCloseOnSelect();
      this.searchText = !this.multiple ? this.getItemText(item, 'set-selected') : closeDropdown ? '' : this.searchText;
      if (closeDropdown) {
        this.currentItem = item;
        this.hideDropdown();
      }
      if (emitInput) {
        this.emitInput();
      }
    },
    handleSplitInput (item, itemValue) {
      if (!this.canSplitItem(item)) return false;

      const splitStringParts = this.getItemText(item).split(this.splitter);
      const selectedItemsCount = Object.keys(this.selectedItemsMap).length;
      const remainingLimit = this.limit ? this.limit - selectedItemsCount : splitStringParts.length;

      splitStringParts.slice(0, remainingLimit).forEach((splitString, idx) => {
        const stringVal = `${itemValue}_${idx}`;
        const splitItem = {
          text: splitString,
          value: stringVal
        };
        this.setValueInMap(stringVal, splitItem);
        this.$emit('select', splitItem);
      });

      this.searchText = '';
      this.hideDropdown();
      this.emitInput();
      return true;
    },
    canSplitItem (item) {
      return this.selectedItemsMap && this.splitter && this.getItemText(item).includes(this.splitter);
    },
    emitInput () {
      const value = this.getCurrentValue();
      this.latestChangedValue = value;
      this.$emit('input', value);
      this.$emit('change', value);
      this.formattedMultipleSelectedOptionsText = this.getMultipleOptionsText();
      this.$forceUpdate();
    },
    setValueInMap (key, item) {
      if (key !== null && item !== undefined) {
        this.selectedItemsMap[JSON.stringify(key)] = item;
      }
    },
    removeValueFromMap (key) {
      delete this.selectedItemsMap[JSON.stringify(key)];
    },
    getCurrentValue () {
      const values = Object.keys(this.selectedItemsMap);
      if (!this.multiple && values[0] === undefined) {
        return null;
      }
      const valuesParsed = values.map(value => JSON.parse(value))

      return this.multiple ? valuesParsed : valuesParsed[0];
    },
    evaluateCloseOnSelect () {
      if (this.closeOnSelect instanceof Function) {
        return this.closeOnSelect(this);
      }

      return !!this.closeOnSelect;
    },
    getMultipleOptionsText () {
      if (!this.multiple) {
        return '';
      }
      const selectedItemsCount = Object.keys(this.selectedItemsMap).length;
      if (!selectedItemsCount) {
        return '';
      }

      let text = this.multipleSelectedOptionsText();
      if (text.includes('{itemCount}')) {
        text = text.replace('{itemCount}', selectedItemsCount);
      }

      if (text.includes('{pluralizedCountText}')) {
        text = text.replace(
          '{pluralizedCountText}',
          selectedItemsCount === 1 ? this.$t('advanced-select.option') : this.$t('advanced-select.options')
        );
      }

      if (text.includes('{pluralizedSelectedText}')) {
        text = text.replace(
          '{pluralizedSelectedText}',
          selectedItemsCount === 1 ? this.$t('advanced-select.chosen') : this.$t('advanced-select.chosen_multiple')
        );
      }

      return text;
    },

    addMultiOptionsText () {
      if (this.isMultipleAddNewOption && this.searchText !== '') {
        const isItem = this.filteredItems.find((el) => el.text === this.searchText);
        if (isItem === undefined) {
          const data = {
            text: this.searchText,
            value: 'new_' + this.number
          }
          this.number++;
          this.activeOptionIndex = null;
          this.handleItemToggle(data);
        }
      }
    },

    sortItems () {
      return this.sortFunction ? this.items.sort(this.sortFunction) : this.items;
    }
  },
  computed: {
    mainTooltip () {
      let value = '';
      if (this.currentItem && this.showMainTooltip) {
        value = this.getItemText(this.currentItem)
      }

      return value;
    },
    sortFunction (a, b) {
      if (!this.sortByAbc && (!this.sortExactSearchResultsAtTop || !this.searchText)) {
        return null;
      }

      if (this.sortByAbc && (!this.sortExactSearchResultsAtTop || !this.searchText)) {
        return (a, b) => a[this.textField].localeCompare(b[this.textField]);
      }

      return (a, b) => {
        const aStartsWithSearchText = a[this.textField].toLocaleLowerCase().startsWith(this.searchText.toLocaleLowerCase());
        const bStartsWithSearchText = b[this.textField].toLocaleLowerCase().startsWith(this.searchText.toLocaleLowerCase());
        if (this.sortByAbc && (aStartsWithSearchText === bStartsWithSearchText)) {
          return a[this.textField].localeCompare(b[this.textField]);
        }
        return bStartsWithSearchText - aStartsWithSearchText;
      }
    },
    isInputVisible () {
      if (!this.multiple) {
        return true;
      }

      if (!this.searchable && this.taggable && this.latestChangedValue != undefined && this.latestChangedValue.length) {
        return false;
      }

      if (!this.isDropdownVisible && this.taggable && this.latestChangedValue != undefined && this.latestChangedValue.length) {
        return false;
      }

      return true;
    },
    filteredItems () {
      if (!this.searchable || !this.isSearchStarted) {
        return this.sortItems();
      }
      if (this.customSearchHandler) {
        return this.customSearchHandler(this.searchText, this.sortItems());
      }

      return this.sortItems().filter((item) => {
        const itemText = this.getItemText(item) ?? '';
        return itemText.toLowerCase().includes(this.searchText.toLowerCase());
      });
    },
    inputPlaceholder () {
      if (!this.searchable) {
        return '';
      }

      if (this.isMultipleAddNewOption) {
        return this.$t('advanced-select.textMultipleAddNewOption');
      }

      if (this.multiple && !this.taggable && !this.isDropdownVisible) {
        return this.formattedMultipleSelectedOptionsText;
      }

      return this.$t(this.searchPlaceholder);
    },
    dropdownWrapperStyle () {
      if (this.dropdownAlign !== 'right') {
        return { left: '0' };
      }

      return { right: '0' };
    },

    isMultipleAddNewOption () {
      return !!(this.multipleAddNewOption && this.multiple && this.searchable);
    }
  },

  mounted () {
    this.setSelectedItems();
  },

  watch: {
    searchText (value, previousValue) {
      if (this.upperCase) {
        this.searchText = value.toUpperCase();
        value = value.toUpperCase();
      }
      if (this.isMultipleAddNewOption) {
        const item = this.filteredItems.find((item) => item.text === value);
        this.temporaryOption = [];
        if (item === undefined && value !== '') {
          this.temporaryOption.push({
            text: value,
            value: 'new_' + this.number
          });
          this.number++;
        }
      }

      this.isSearchStarted = true;
      if (this.autoSelectOnlyOptionOnSearch && value) {
        this.$nextTick(() => {
          if (this.filteredItems.length === 1 && !this.isItemSelected(this.filteredItems[0])) {
            this.handleItemToggle(this.filteredItems[0]);
          }
        })
      }
      if (value !== previousValue) {
        this.activeOptionIndex = null;
        this.$emit('searchTextChange', value);
      }
    },
    items () {
      if (!this.autoAdjustValuesToItems) {
        return;
      }

      if (!(this.multipleAddNewOption && this.multiple && this.searchable)) {
        this.setSelectedItems();
      }
    },
    value (newValue) {
      if (newValue === this.latestChangedValue) {
        return;
      }
      this.setSelectedItems();
    },
    isDropdownVisible (value) {
      const eventType = value ? 'dropdownShow' : 'dropdownHide';
      this.$emit(eventType);
    },
    isLoadingOption: {
      immediate: true,
      handler (val) {
      }
    }
  }
}
